<template>
    <TransitionRoot as="div" :show="isOpen">
        <div class="fixed inset-0 bg-black/50" @click="closeDrawer" style="z-index: 200 !important"></div>
        <TransitionChild
            as="div"
            enter="transition ease-out duration-200 transform"
            enter-from="translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in duration-200 transform"
            leave-from="translate-x-0"
            leave-to="translate-x-full">
            <div class="fixed inset-y-0 right-0 w-5/12 bg-white shadow-lg" :style="{ width: drawerWidth + 'px', zIndex: 200 }" @click.stop>
                <div class="absolute left-0 top-0 h-full w-2 cursor-ew-resize" @mousedown="startResizing"></div>

                <div class="pl-4 flex flex-col h-full">
                    <div class="flex justify-between items-center">
                        <h2 class="text-lg font-medium mb-4">Message Details</h2>
                        <button aria-label="Close" class="mb-4 p-2 text-gray-600 hover:text-gray-700" @click="closeDrawer">
                            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>
                    <div class="grow overflow-y-auto">
                        <Tabs :tabs="tabs">
                            <template #Metadata>
                                <ul class="divide-y divide-gray-200 font-mono text-xs">
                                    <li class="flex items center gap-2 p-4">
                                        <span class="font-semibold">Offset:</span>
                                        <span>{{ message?.offset }}</span>
                                    </li>
                                    <li class="flex items center gap-2 p-4">
                                        <span class="font-semibold">Partition:</span>
                                        <span>{{ message?.partition }}</span>
                                    </li>
                                    <li class="flex items center gap-2 p-4">
                                        <span class="font-semibold">Timestamp:</span>
                                        <span>{{ message?.timestamp }}</span>
                                    </li>
                                    <li class="flex items center gap-2 p-4">
                                        <span class="font-semibold">Timestamp Type:</span>
                                        <span>{{ message?.timestamp_type }}</span>
                                    </li>
                                    <li class="flex flex-col items center gap-2 p-4">
                                        <span class="font-semibold">Sizes:</span>
                                        <div class="flex items center gap-2 ml-5">
                                            <span>Headers:</span>
                                            <span>{{ formatter(message?.serialized_header_size, { unit: 'bytes' }).display }}</span>
                                        </div>
                                        <div class="flex items center gap-2 ml-5">
                                            <span>Key:</span>
                                            <span>{{ formatter(message?.serialized_key_size, { unit: 'bytes' }).display }}</span>
                                        </div>
                                        <div class="flex items center gap-2 ml-5">
                                            <span>Value:</span>
                                            <span>{{ formatter(message?.serialized_value_size, { unit: 'bytes' }).display }}</span>
                                        </div>
                                    </li>
                                </ul>
                            </template>
                            <template #Key>
                                <div class="py-4">
                                    <div class="flex items-center gap-2 mb-3">
                                        <button
                                            aria-label="button"
                                            v-if="!keyExpanded && isValidJSON(message?.key)"
                                            class="btn btn-drawer"
                                            @click="
                                                collapseOnDepth = undefined;
                                                keyExpanded = !keyExpanded;
                                            ">
                                            <PlusIcon class="h-5 w-5 mr-1" /> Expand all
                                        </button>
                                        <button
                                            aria-label="button"
                                            v-if="keyExpanded && isValidJSON(message?.key)"
                                            class="btn btn-drawer"
                                            @click="
                                                collapseOnDepth = 0;
                                                keyExpanded = !keyExpanded;
                                            ">
                                            <MinusIcon class="h-5 w-5 mr-1" /> Collapse all
                                        </button>
                                        <div title="Copy" class="flex gap-1 items-center">
                                            <ClipboardIcon
                                                class="h-5 w-5 mr-1 text-gray-600 cursor-pointer hover:text-cyan-900 flex items-center"
                                                @click="copyToClipboard(message?.key)" />
                                            <span v-if="copyDone" class="text-xs text-gray-600">Copied!</span>
                                        </div>
                                    </div>
                                    <VueJsonPretty
                                        v-if="isValidJSON(message?.key)"
                                        :data="message?.key"
                                        :show-line-number="true"
                                        :show-icon="true"
                                        :show-length="true"
                                        collapsed-on-click-brackets="true"
                                        :deep="collapseOnDepth" />

                                    <span v-else>{{ message?.key }}</span>
                                </div>
                            </template>
                            <template #Value>
                                <div class="py-4">
                                    <div class="flex items-center gap-2 mb-3">
                                        <button
                                            aria-label="button"
                                            v-if="!valueExpanded && isValidJSON(message?.value)"
                                            class="btn btn-drawer"
                                            @click="
                                                collapseOnDepth = undefined;
                                                valueExpanded = !valueExpanded;
                                            ">
                                            <PlusIcon class="h-5 w-5 mr-1" /> Expand all
                                        </button>
                                        <button
                                            aria-label="button"
                                            v-if="valueExpanded && isValidJSON(message?.value)"
                                            class="btn btn-drawer"
                                            @click="
                                                collapseOnDepth = 0;
                                                valueExpanded = !valueExpanded;
                                            ">
                                            <MinusIcon class="h-5 w-5 mr-1" /> Collapse all
                                        </button>
                                        <div title="Copy" class="flex gap-1 items-center">
                                            <ClipboardIcon
                                                class="h-5 w-5 mr-1 text-gray-600 cursor-pointer hover:text-cyan-900 flex items-center"
                                                @click="copyToClipboard(message?.value)" />
                                            <span v-if="copyDone" class="text-xs text-gray-600">Copied!</span>
                                        </div>
                                    </div>
                                    <VueJsonPretty
                                        v-if="isValidJSON(message?.value)"
                                        :data="message?.value"
                                        :show-line-number="true"
                                        :show-icon="true"
                                        :show-length="true"
                                        collapsed-on-click-brackets="true"
                                        :deep="collapseOnDepth" />

                                    <span v-else>{{ message?.value }}</span>
                                </div>
                            </template>

                            <template #value_schema>
                                <div class="py-4">
                                    <div class="flex items-center gap-2 mb-3">
                                        <button
                                            aria-label="button"
                                            v-if="!valueSchemaExpanded && isValidJSON(message?.value_schema)"
                                            class="btn btn-drawer"
                                            @click="
                                                collapseOnDepth = undefined;
                                                valueSchemaExpanded = !valueSchemaExpanded;
                                            ">
                                            <PlusIcon class="h-5 w-5 mr-1" /> Expand all
                                        </button>
                                        <button
                                            aria-label="button"
                                            v-if="valueSchemaExpanded && isValidJSON(message?.value_schema)"
                                            class="btn btn-drawer"
                                            @click="
                                                collapseOnDepth = 0;
                                                valueSchemaExpanded = !valueSchemaExpanded;
                                            ">
                                            <MinusIcon class="h-5 w-5 mr-1" /> Collapse all
                                        </button>
                                        <div title="Copy" class="flex gap-1 items-center">
                                            <ClipboardIcon
                                                class="h-5 w-5 mr-1 text-gray-600 cursor-pointer hover:text-cyan-900 flex items-center"
                                                @click="copyToClipboard(message?.value_schema)" />
                                            <span v-if="copyDone" class="text-xs text-gray-600">Copied!</span>
                                        </div>
                                    </div>
                                    <VueJsonPretty
                                        v-if="isValidJSON(message?.value_schema)"
                                        :data="message?.value_schema"
                                        :show-line-number="true"
                                        :show-icon="true"
                                        :show-length="true"
                                        collapsed-on-click-brackets="true"
                                        :deep="collapseOnDepth" />

                                    <span v-else>{{ message?.value_schema }}</span>
                                </div>
                            </template>

                            <template #key_schema>
                                <div class="py-4">
                                    <div class="flex items-center gap-2 mb-3">
                                        <button
                                            aria-label="button"
                                            v-if="!keySchemaExpanded && isValidJSON(message?.key_schema)"
                                            class="btn btn-drawer"
                                            @click="
                                                collapseOnDepth = undefined;
                                                keySchemaExpanded = !keySchemaExpanded;
                                            ">
                                            <PlusIcon class="h-5 w-5 mr-1" /> Expand all
                                        </button>
                                        <button
                                            aria-label="button"
                                            v-if="keySchemaExpanded && isValidJSON(message?.key_schema)"
                                            class="btn btn-drawer"
                                            @click="
                                                collapseOnDepth = 0;
                                                keySchemaExpanded = !keySchemaExpanded;
                                            ">
                                            <MinusIcon class="h-5 w-5 mr-1" /> Collapse all
                                        </button>
                                        <div title="Copy" class="flex gap-1 items-center">
                                            <ClipboardIcon
                                                class="h-5 w-5 mr-1 text-gray-600 cursor-pointer hover:text-cyan-900 flex items-center"
                                                @click="copyToClipboard(message?.key_schema)" />
                                            <span v-if="copyDone" class="text-xs text-gray-600">Copied!</span>
                                        </div>
                                    </div>
                                    <VueJsonPretty
                                        v-if="isValidJSON(message?.key_schema)"
                                        :data="message?.key_schema"
                                        :show-line-number="true"
                                        :show-icon="true"
                                        :show-length="true"
                                        collapsed-on-click-brackets="true"
                                        :deep="collapseOnDepth" />

                                    <span v-else>{{ message?.key_schema }}</span>
                                </div>
                            </template>

                            <template #Headers>
                                <div class="py-4">
                                    <div class="flex items-center gap-2 mb-3">
                                        <button
                                            aria-label="button"
                                            v-if="!headersExpanded && isValidJSON(message?.headers)"
                                            class="btn btn-drawer"
                                            @click="
                                                collapseOnDepth = undefined;
                                                headersExpanded = !headersExpanded;
                                            ">
                                            <PlusIcon class="h-5 w-5 mr-1" /> Expand all
                                        </button>
                                        <button
                                            aria-label="button"
                                            v-if="headersExpanded && isValidJSON(message?.value)"
                                            class="btn btn-drawer"
                                            @click="
                                                collapseOnDepth = 0;
                                                headersExpanded = !headersExpanded;
                                            ">
                                            <MinusIcon class="h-5 w-5 mr-1" /> Collapse all
                                        </button>
                                        <div title="Copy" class="flex gap-1 items-center">
                                            <ClipboardIcon
                                                class="h-5 w-5 mr-1 text-gray-600 cursor-pointer hover:text-cyan-900 flex items-center"
                                                @click="copyToClipboard(message?.headers)" />
                                            <span v-if="copyDone" class="text-xs text-gray-600">Copied!</span>
                                        </div>
                                    </div>
                                    <VueJsonPretty
                                        v-if="isValidJSON(message?.headers)"
                                        :data="message?.headers"
                                        :show-line-number="true"
                                        :show-icon="true"
                                        :show-length="true"
                                        collapsed-on-click-brackets="true"
                                        :deep="collapseOnDepth" />

                                    <span v-else>{{ message?.headers }}</span>
                                </div>
                            </template>
                        </Tabs>
                    </div>
                </div>
            </div>
        </TransitionChild>
    </TransitionRoot>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { TransitionChild, TransitionRoot } from '@headlessui/vue';
import { ClipboardIcon, PlusIcon, MinusIcon } from '@heroicons/vue/24/outline';
import VueJsonPretty from 'vue-json-pretty';
import Tabs from '@/components/global/Tabs.vue';
import { formatter } from '@/utils/utils';

const props = defineProps({
    message: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['close']);

const collapseOnDepth = ref(1);
const keyExpanded = ref(false);
const valueExpanded = ref(false);
const headerExpanded = ref(false);
const valueSchemaExpanded = ref(false);
const keySchemaExpanded = ref(false);

const isOpen = ref(false);
const tabs = ['Metadata', 'Key', 'Value', { name: 'Value Schema', key: 'value_schema' }, { name: 'Key Schema', key: 'key_schema' }, 'Headers'];
const copyDone = ref(false);

const drawerWidth = ref(700); // Default drawer width in pixels
const MIN_WIDTH = 300; // Minimum width in pixels
const MAX_WIDTH = 1000; // Maximum width in pixels
const isResizing = ref(false);

const isValidJSON = (json) => {
    try {
        JSON.parse(JSON.stringify(json));
        return true;
    } catch (e) {
        return false;
    }
};

watch(
    () => props.message,
    () => {
        keyExpanded.value = false;
        valueExpanded.value = false;
        headerExpanded.value = false;
        valueSchemaExpanded.value = false;
        keySchemaExpanded.value = false;
    }
);

function copyToClipboard(textToCopy) {
    copyDone.value = true;
    navigator.clipboard
        .writeText(textToCopy && typeof textToCopy === 'object' ? JSON.stringify(textToCopy, null, 2) : textToCopy)
        .then(() => setTimeout(() => (copyDone.value = false), 1000))
        .catch((err) => {
            console.error('Could not copy text: ', err);
        });
}

const closeDrawer = () => {
    isOpen.value = false;
    emit('close');
};

watch(
    () => props.message,
    (newVal) => {
        isOpen.value = !!newVal;
    }
);

onMounted(() => {
    isOpen.value = !!props.message;
});

function startResizing() {
    isResizing.value = true;

    document.addEventListener('mousemove', resizeDrawer);
    document.addEventListener('mouseup', stopResizing);
}

function resizeDrawer(e) {
    if (!isResizing.value) return;
    // For a right-anchored drawer, the width is the distance from the mouse's x position to the right edge.
    let newWidth = window.innerWidth - e.clientX;
    // Clamp the new width to a min and max value.
    if (newWidth < MIN_WIDTH) newWidth = MIN_WIDTH;
    if (newWidth > MAX_WIDTH) newWidth = MAX_WIDTH;
    drawerWidth.value = newWidth;
}

function stopResizing() {
    isResizing.value = false;
    document.removeEventListener('mousemove', resizeDrawer);
    document.removeEventListener('mouseup', stopResizing);
}
</script>

<style scoped>
@reference '../../style.css';

.btn-drawer {
    @apply cursor-pointer inline-flex items-center text-sm px-2 py-1 border border-gray-300 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 shadow-sm whitespace-nowrap;
    transition: background-color 0.2s;
}
.fixed {
    will-change: transform;
}
</style>
